"use client";

/**
 * Unified Phone Number Management Page
 *
 * Combines three phone number workflows into a single integrated page:
 *
 * 1. PROVISIONING — Search and provision new Twilio numbers for the org
 * 2. USER ASSIGNMENT — Assign dedicated numbers to individual staff members
 *    (per-user mode) so each agent sends from their own number
 * 3. ROUTING — Assign numbers to specific campaigns or programs so all
 *    outbound messages for that campaign/program use a specific sender number
 *
 * The phone number resolver uses this priority:
 *   Campaign/Program assignment → User-specific number → Org provider → Platform default
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Phone, Plus, Trash2, Search, Loader2, X, Link2,
  Building2, User, Megaphone, Briefcase, CheckCircle,
  MapPin, DollarSign, Users, ArrowRight, MessageSquare, Shield,
  PhoneIncoming
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { InboundRoutingPanel } from '@/components/settings/InboundRoutingPanel';
import { VerifyNumberModal } from '@/components/settings/VerifyNumberModal';

// ── Types ───────────────────────────────────────────────────────────

interface ProvisionedNumber {
  id: string;
  phone_number: string;
  phone_sid: string | null;
  friendly_name: string | null;
  capabilities: Record<string, any>;
  assigned_to: 'organization' | 'user';
  user_id: number | null;
  user_email: string | null;
  created_at: string | null;
  messaging_service_sid?: string | null;
  sms_webhook_url?: string | null;
  voice_webhook_url?: string | null;
}

interface UserPhoneNumber {
  id: string;
  user_id: number;
  phone_number: string;
  phone_number_type: string;
  provider_type: string | null;
  friendly_name: string | null;
  is_active: boolean;
  is_primary: boolean;
  user_email: string | null;
  user_name: string | null;
  verification_status?: string;
  verification_method?: string | null;
}

interface SenderAssignment {
  id: string;
  phone_number: string;
  provider_id: string | null;
  assignment_type: string;
  assignment_id: string | null;
  channel: string;
  created_at: string | null;
}

interface AvailableNumber {
  phone_number: string;
  friendly_name: string;
  locality: string | null;
  region: string | null;
  capabilities: { voice: boolean; sms: boolean; mms: boolean };
  number_type: string;
}

interface ProgramOption { id: number; name: string; }
interface CampaignOption { id: string; name: string; status: string; }
interface OrgUser { id: number; name: string; email: string; }

// ── Constants ───────────────────────────────────────────────────────

const CHANNEL_BADGES: Record<string, { label: string; color: string }> = {
  sms: { label: 'SMS', color: 'bg-green-100 text-green-700' },
  whatsapp: { label: 'WhatsApp', color: 'bg-green-50 text-green-700' },
  rcs: { label: 'RCS', color: 'bg-indigo-100 text-indigo-700' },
};

type ActiveTab = 'overview' | 'provision' | 'user-assign' | 'routing' | 'inbound';

// ── Helpers ─────────────────────────────────────────────────────────

const formatPhone = (phone: string) => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// ── Component ───────────────────────────────────────────────────────

interface PhoneNumberManagementProps {
  /** Org being viewed — passed by the parent Organization Settings page so this
   *  tab is scoped to the SAME org as every sibling tab. Falls back to the
   *  active_cbo_id localStorage key only when no prop is supplied. */
  organizationId?: number;
}

export const PhoneNumberManagement: React.FC<PhoneNumberManagementProps> = ({ organizationId: orgIdProp }) => {
  const organizationId = orgIdProp
    ?? (localStorage.getItem('active_cbo_id') ? Number(localStorage.getItem('active_cbo_id')) : null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [loading, setLoading] = useState(true);

  // Data
  const [provisionedNumbers, setProvisionedNumbers] = useState<ProvisionedNumber[]>([]);
  const [userPhoneNumbers, setUserPhoneNumbers] = useState<UserPhoneNumber[]>([]);
  const [resolvedDefault, setResolvedDefault] = useState<{ from_number: string; resolution_source: string } | null>(null);
  const [assignments, setAssignments] = useState<SenderAssignment[]>([]);
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);

  // Provisioning
  const [searchMode, setSearchMode] = useState(false);
  const [searching, setSearching] = useState(false);
  const [provisioning, setProvisioning] = useState<string | null>(null);
  const [availableNumbers, setAvailableNumbers] = useState<AvailableNumber[]>([]);
  const [areaCode, setAreaCode] = useState('');
  const [numberType, setNumberType] = useState('local');

  // Routing form
  const [showRoutingForm, setShowRoutingForm] = useState(false);
  const [routingPhone, setRoutingPhone] = useState('');
  const [routingChannel, setRoutingChannel] = useState('sms');
  const [routingType, setRoutingType] = useState('organization');
  const [routingSelectedIds, setRoutingSelectedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // User assignment form
  const [showUserAssignForm, setShowUserAssignForm] = useState(false);
  const [assignPhone, setAssignPhone] = useState('');
  const [assignUserId, setAssignUserId] = useState('');
  // BYO = the staff member's OWN (non-ported) number. When true the number is
  // created unverified (requires_verification) and we open the ownership
  // verification modal on it right after — instead of silently marking it verified.
  const [assignByo, setAssignByo] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState<UserPhoneNumber | null>(null);

  const [error, setError] = useState<string | null>(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'X-CBO-ID': String(organizationId || ''),
  }), [organizationId]);

  // ── Data fetching ─────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    const h = headers();

    // Fetch all in parallel
    const [provRes, userRes, assignRes, progRes, campRes, usersRes, resolveRes] = await Promise.allSettled([
      fetch(getWeaveinApiUrl(`/api/phone-provisioning/organization/${organizationId}`), { headers: h }),
      fetch(getWeaveinApiUrl(`/api/user-phone-numbers/organization/${organizationId}`), { headers: h }),
      fetch(getWeaveinApiUrl(`/api/communication-providers/sender-assignments?organization_id=${organizationId}`), { headers: h }),
      fetch(getWeaveinApiUrl(`/api/programs?cbo_id=${organizationId}`), { headers: h }),
      fetch(getWeaveinApiUrl(`/api/campaigns?organization_id=${organizationId}`), { headers: h }),
      // staff_only=true → exclude portal members; the "assign to staff" picker
      // is for staff users only (super/ops admins included by the backend).
      fetch(getWeaveinApiUrl(`/api/cbos/${organizationId}/users?staff_only=true`), { headers: h }),
      fetch(getWeaveinApiUrl(`/api/user-phone-numbers/resolve/${organizationId}?channel=sms`), { headers: h }),
    ]);

    if (provRes.status === 'fulfilled' && provRes.value.ok)
      setProvisionedNumbers(await provRes.value.json());
    if (userRes.status === 'fulfilled' && userRes.value.ok)
      setUserPhoneNumbers(await userRes.value.json());
    if (assignRes.status === 'fulfilled' && assignRes.value.ok)
      setAssignments(await assignRes.value.json());
    if (progRes.status === 'fulfilled' && progRes.value.ok) {
      const d = await progRes.value.json();
      setPrograms((Array.isArray(d) ? d : d.programs || []).map((p: any) => ({
        id: p.id, name: p.name || p.program_name || `Program ${p.id}`,
      })));
    }
    if (campRes.status === 'fulfilled' && campRes.value.ok) {
      const d = await campRes.value.json();
      setCampaigns((Array.isArray(d) ? d : d.campaigns || []).map((c: any) => ({
        id: c.id, name: c.name, status: c.status,
      })));
    }
    if (usersRes.status === 'fulfilled' && usersRes.value.ok) {
      const d = await usersRes.value.json();
      setOrgUsers((Array.isArray(d) ? d : d.users || []).map((u: any) => ({
        id: u.id, name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email, email: u.email,
      })));
    }
    if (resolveRes.status === 'fulfilled' && resolveRes.value.ok) {
      const d = await resolveRes.value.json();
      if (d.from_number) {
        setResolvedDefault({ from_number: d.from_number, resolution_source: d.resolution_source || 'platform_default' });
      }
    }

    setLoading(false);
  }, [organizationId, headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── All org numbers (merged + deduped) ────────────────────────────

  const allNumbers = (() => {
    const seen = new Set<string>();
    const list: { phone: string; source: string; userName?: string; capabilities?: Record<string, any>; isDefault?: boolean }[] = [];

    // Platform default / resolved number (highest visibility — this is the active sender)
    if (resolvedDefault?.from_number) {
      seen.add(resolvedDefault.from_number);
      const sourceLabel = resolvedDefault.resolution_source === 'platform_default'
        ? 'Platform Default (Twilio)' : resolvedDefault.resolution_source === 'organization'
        ? 'Organization Provider' : resolvedDefault.resolution_source;
      list.push({
        phone: resolvedDefault.from_number,
        source: sourceLabel,
        isDefault: true,
        capabilities: { sms: true, voice: true },
      });
    }

    // Provisioned numbers
    provisionedNumbers.forEach(n => {
      if (!seen.has(n.phone_number)) {
        seen.add(n.phone_number);
        list.push({
          phone: n.phone_number,
          source: n.assigned_to === 'user' ? `User: ${n.user_email || 'assigned'}` : 'Organization',
          userName: n.user_email || undefined,
          capabilities: n.capabilities,
        });
      }
    });

    // User-assigned numbers
    userPhoneNumbers.forEach(n => {
      if (!seen.has(n.phone_number)) {
        seen.add(n.phone_number);
        list.push({
          phone: n.phone_number,
          source: `User: ${n.user_name || n.user_email || 'assigned'}`,
          userName: n.user_name || n.user_email || undefined,
        });
      }
    });
    return list;
  })();

  // ── Provisioning actions ──────────────────────────────────────────

  const searchNumbers = async () => {
    setSearching(true); setError(null); setAvailableNumbers([]);
    try {
      const res = await fetch(getWeaveinApiUrl('/api/phone-provisioning/search'), {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          country: 'US',
          area_code: areaCode || undefined,
          number_type: numberType,
          // Don't filter on capability — Twilio treats False as exact-match,
          // which silently drops most numbers. Backend now mirrors this.
          limit: 20,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableNumbers(data);
        if (data.length === 0) {
          setError(`No ${numberType.replace('_', '-')} numbers available${areaCode ? ` in area code ${areaCode}` : ''}. Try a different area code or number type.`);
        }
      } else {
        let detail = `Search failed (HTTP ${res.status})`;
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch { /* response wasn't JSON */ }
        setError(detail);
      }
    } catch (e: any) {
      setError(`Network error: ${e?.message || 'could not reach the server'}`);
    }
    finally { setSearching(false); }
  };

  const provisionNumber = async (phoneNumber: string, friendlyName: string) => {
    setProvisioning(phoneNumber); setError(null);
    try {
      const res = await fetch(getWeaveinApiUrl('/api/phone-provisioning/provision'), {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ phone_number: phoneNumber, organization_id: organizationId, friendly_name: friendlyName }),
      });
      if (res.ok) { setAvailableNumbers(prev => prev.filter(n => n.phone_number !== phoneNumber)); fetchAll(); }
      else setError((await res.json()).detail || 'Provision failed');
    } catch { setError('Error provisioning'); }
    finally { setProvisioning(null); }
  };

  const releaseNumber = async (phoneSid: string) => {
    if (!confirm('Release this number? This cannot be undone.')) return;
    try {
      await fetch(getWeaveinApiUrl('/api/phone-provisioning/release'), {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ phone_sid: phoneSid, organization_id: organizationId }),
      });
      fetchAll();
    } catch { setError('Error releasing number'); }
  };

  // ── User assignment actions ───────────────────────────────────────

  const assignToUser = async () => {
    if (!assignPhone || !assignUserId || !organizationId) return;
    setSaving(true);
    try {
      // NOTE: the assign endpoint is POST /api/user-phone-numbers (the router's
      // `POST /`), NOT `/assign`. The old `/assign` path 404'd and the failure
      // was swallowed, so assignments silently never persisted.
      const res = await fetch(getWeaveinApiUrl('/api/user-phone-numbers'), {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          user_id: Number(assignUserId),
          organization_id: organizationId,
          phone_number: assignPhone,
          phone_number_type: 'local',
          provider_type: 'twilio',
          sms_enabled: true,
          voice_enabled: true,
          is_primary: true,
          // BYO numbers must prove ownership (Verified Caller ID / SMS OTP) before
          // the resolver will send from them; platform numbers stay auto-verified.
          requires_verification: assignByo,
        }),
      });
      if (!res.ok) {
        let detail = `Assignment failed (HTTP ${res.status})`;
        try { const b = await res.json(); if (b?.detail) detail = b.detail; } catch { /* not JSON */ }
        setError(detail);
        return;
      }
      const created = await res.json().catch(() => ({}));
      const wasByo = assignByo;
      const newPhone = assignPhone;
      const newId = created?.phone_number_id != null ? String(created.phone_number_id) : null;
      setShowUserAssignForm(false);
      setAssignPhone(''); setAssignUserId(''); setAssignByo(false);
      fetchAll();
      // For a BYO number, jump straight into ownership verification on the new row.
      if (wasByo && newId) {
        setVerifyTarget({
          id: newId,
          user_id: Number(assignUserId),
          phone_number: newPhone,
          phone_number_type: 'local',
          provider_type: 'twilio',
          friendly_name: null,
          is_active: true,
          is_primary: true,
          user_email: null,
          user_name: null,
          verification_status: 'unverified',
          verification_method: null,
        });
      }
    } catch { setError('Failed to assign number'); }
    finally { setSaving(false); }
  };

  // ── Routing actions ───────────────────────────────────────────────

  const createRouting = async () => {
    if (!routingPhone || !organizationId) return;
    setSaving(true);
    try {
      if (routingType === 'organization') {
        await fetch(getWeaveinApiUrl('/api/communication-providers/sender-assignments'), {
          method: 'POST', headers: headers(),
          body: JSON.stringify({ phone_number: routingPhone, organization_id: organizationId, assignment_type: 'organization', assignment_id: null, channel: routingChannel }),
        });
      } else {
        for (const id of routingSelectedIds) {
          await fetch(getWeaveinApiUrl('/api/communication-providers/sender-assignments'), {
            method: 'POST', headers: headers(),
            body: JSON.stringify({ phone_number: routingPhone, organization_id: organizationId, assignment_type: routingType, assignment_id: id, channel: routingChannel }),
          });
        }
      }
      setShowRoutingForm(false);
      setRoutingPhone(''); setRoutingSelectedIds([]);
      fetchAll();
    } catch { setError('Failed to create routing'); }
    finally { setSaving(false); }
  };

  const getAssignmentLabel = (a: SenderAssignment) => {
    if (a.assignment_type === 'organization') return 'Organization-wide';
    if (a.assignment_type === 'campaign') return campaigns.find(c => c.id === a.assignment_id)?.name || `Campaign ${a.assignment_id?.slice(0, 8)}...`;
    if (a.assignment_type === 'program') return programs.find(p => String(p.id) === a.assignment_id)?.name || `Program ${a.assignment_id}`;
    return a.assignment_id || '';
  };

  // ── Tab content ───────────────────────────────────────────────────

  const tabs: { id: ActiveTab; label: string; icon: React.ElementType; description: string }[] = [
    { id: 'overview', label: 'Overview', icon: Phone, description: 'All numbers at a glance' },
    { id: 'provision', label: 'Provision Numbers', icon: Plus, description: 'Get new Twilio numbers' },
    { id: 'user-assign', label: 'Staff Assignment', icon: User, description: 'Assign numbers to staff' },
    { id: 'routing', label: 'Campaign/Program Routing', icon: Link2, description: 'Route numbers to campaigns' },
    { id: 'inbound', label: 'Inbound Routing', icon: PhoneIncoming, description: 'Route incoming calls to case managers' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading phone number management...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page description */}
      <p className="text-[13px] text-ink-500">
        Provision, assign, and route phone numbers for your organization&apos;s messaging
      </p>

      {/* Resolution Priority Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm font-medium text-blue-800 mb-2">How number resolution works</p>
        <div className="flex items-center gap-2 text-xs text-blue-700">
          <span className="px-2 py-1 bg-white rounded border border-blue-200 font-medium">Campaign/Program Route</span>
          <ArrowRight className="w-3 h-3" />
          <span className="px-2 py-1 bg-white rounded border border-blue-200 font-medium">Staff&apos;s Number</span>
          <ArrowRight className="w-3 h-3" />
          <span className="px-2 py-1 bg-white rounded border border-blue-200 font-medium">Org Provider</span>
          <ArrowRight className="w-3 h-3" />
          <span className="px-2 py-1 bg-white rounded border border-blue-200 font-medium">Platform Default</span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b pb-0">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700">
          <X className="w-4 h-4" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ═══════════════ INBOUND ROUTING TAB ═══════════════ */}
      {activeTab === 'inbound' && (
        <InboundRoutingPanel
          organizationId={organizationId}
          numbers={allNumbers.map(n => n.phone)}
        />
      )}

      {/* ═══════════════ OVERVIEW TAB ═══════════════ */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{allNumbers.length}</p>
              <p className="text-sm text-gray-500">Total Numbers</p>
            </div>
            <div className="bg-white border rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{userPhoneNumbers.length}</p>
              <p className="text-sm text-gray-500">Staff Assigned</p>
            </div>
            <div className="bg-white border rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{assignments.length}</p>
              <p className="text-sm text-gray-500">Campaign/Program Routes</p>
            </div>
          </div>

          {/* All numbers list */}
          {allNumbers.length === 0 ? (
            <div className="text-center py-12 text-gray-500 border border-dashed rounded-lg">
              <Phone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium text-lg">No phone numbers configured</p>
              <p className="text-sm mt-1 mb-4">Get started by provisioning a Twilio number</p>
              <button onClick={() => setActiveTab('provision')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                <Plus className="w-4 h-4 inline mr-1" /> Provision a Number
              </button>
            </div>
          ) : (
            <div className="bg-white border rounded-lg divide-y">
              {allNumbers.map((n, i) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Phone className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold text-gray-900">{formatPhone(n.phone)}</span>
                        {n.isDefault && (
                          <span className="text-xs px-1.5 py-0.5 bg-blue-600 text-white rounded font-medium">Active Default</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{n.source}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {n.capabilities?.sms && <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">SMS</span>}
                    {n.capabilities?.voice && <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Voice</span>}
                    {/* Show routing count */}
                    {assignments.filter(a => a.phone_number === n.phone).length > 0 && (
                      <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                        {assignments.filter(a => a.phone_number === n.phone).length} route{assignments.filter(a => a.phone_number === n.phone).length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════ PROVISION TAB ═══════════════ */}
      {activeTab === 'provision' && (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Provision New Numbers</h3>
            <p className="text-xs text-gray-500">Search Twilio&apos;s inventory and provision numbers for your organization. Local numbers start at $1.15/mo, toll-free at $2.15/mo.</p>
          </div>

          {/* Search */}
          <div className="bg-white border rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Area Code</label>
                <input type="text" value={areaCode} onChange={e => setAreaCode(e.target.value)} placeholder="e.g., 650" maxLength={3}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number Type</label>
                <select value={numberType} onChange={e => setNumberType(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="local">Local</option>
                  <option value="toll_free">Toll-Free</option>
                  <option value="mobile">Mobile</option>
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={searchNumbers} disabled={searching}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 text-sm">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
                </button>
              </div>
            </div>

            {availableNumbers.length > 0 && (
              <div className="max-h-64 overflow-y-auto space-y-2 mt-4">
                {availableNumbers.map(n => (
                  <div key={n.phone_number} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                    <div>
                      <span className="font-mono font-medium">{formatPhone(n.phone_number)}</span>
                      {n.locality && <span className="text-xs text-gray-500 ml-2"><MapPin className="w-3 h-3 inline" /> {n.locality}, {n.region}</span>}
                    </div>
                    <button onClick={() => provisionNumber(n.phone_number, n.friendly_name)} disabled={provisioning === n.phone_number}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50">
                      {provisioning === n.phone_number ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Provision
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Provisioned numbers — merge org-level rows with user assignments
              so each phone number appears once with its full status */}
          {(() => {
            // Build the merged list: dedupe by phone, prefer the user-assigned
            // view if both exist (the user attribution is the more interesting
            // status for an admin reviewing inventory).
            type Merged = {
              key: string;
              phone: string;
              phone_sid: string | null;
              friendly_name: string | null;
              capabilities: Record<string, any>;
              assignedUser: { name?: string | null; email?: string | null } | null;
              messaging_service_sid?: string | null;
              sms_webhook_url?: string | null;
              voice_webhook_url?: string | null;
            };
            const map = new Map<string, Merged>();
            provisionedNumbers.forEach(n => {
              const existing = map.get(n.phone_number);
              const next: Merged = {
                key: n.id,
                phone: n.phone_number,
                phone_sid: n.phone_sid,
                friendly_name: n.friendly_name,
                capabilities: n.capabilities || {},
                assignedUser: n.assigned_to === 'user'
                  ? { email: n.user_email }
                  : existing?.assignedUser || null,
                messaging_service_sid: n.messaging_service_sid || existing?.messaging_service_sid || null,
                sms_webhook_url: n.sms_webhook_url || existing?.sms_webhook_url || null,
                voice_webhook_url: n.voice_webhook_url || existing?.voice_webhook_url || null,
              };
              if (!existing || next.assignedUser) map.set(n.phone_number, next);
            });
            userPhoneNumbers.forEach(n => {
              const existing = map.get(n.phone_number);
              const assignedUser = { name: n.user_name, email: n.user_email };
              if (existing) {
                existing.assignedUser = assignedUser;
                existing.phone_sid = existing.phone_sid || null;
              } else {
                map.set(n.phone_number, {
                  key: n.id,
                  phone: n.phone_number,
                  phone_sid: null,
                  friendly_name: n.friendly_name,
                  capabilities: {
                    sms: !!(n as any).sms_enabled,
                    voice: !!(n as any).voice_enabled,
                    mms: !!(n as any).mms_enabled,
                  },
                  assignedUser,
                });
              }
            });
            const merged = Array.from(map.values());

            return (
              <div className="bg-white border rounded-lg">
                <div className="px-4 py-3 border-b">
                  <h4 className="text-sm font-semibold text-gray-700">
                    Provisioned Numbers ({merged.length})
                  </h4>
                </div>
                {merged.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Phone className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No provisioned numbers yet</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {merged.map(n => (
                      <div key={n.key} className="px-4 py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`p-1.5 rounded ${n.assignedUser ? 'bg-purple-100' : 'bg-blue-100'}`}>
                            {n.assignedUser
                              ? <User className="w-4 h-4 text-purple-600" />
                              : <Building2 className="w-4 h-4 text-blue-600" />}
                          </div>
                          <div className="min-w-0">
                            <span className="font-mono text-sm font-medium">{formatPhone(n.phone)}</span>
                            <p className="text-xs text-gray-500 truncate">
                              {n.assignedUser
                                ? `Assigned to ${n.assignedUser.name || n.assignedUser.email || 'staff member'}`
                                : 'Organization number — unassigned'}
                              {n.friendly_name ? ` — ${n.friendly_name}` : ''}
                            </p>
                            {/* A2P 10DLC + webhook setup status. A2P messaging
                                service is REQUIRED for US SMS delivery (incl.
                                login 2FA codes) — flag when missing. */}
                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                              {n.messaging_service_sid ? (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700"
                                  title={`A2P Messaging Service: ${n.messaging_service_sid}`}
                                >
                                  ✓ A2P / Messaging Service
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                                  title="No A2P 10DLC Messaging Service attached — US carriers will filter SMS (2FA codes won't deliver)."
                                >
                                  ⚠ No Messaging Service
                                </span>
                              )}
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${n.sms_webhook_url ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                                title={n.sms_webhook_url || 'No inbound SMS webhook configured'}
                              >
                                {n.sms_webhook_url ? '✓' : '—'} SMS webhook
                              </span>
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${n.voice_webhook_url ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                                title={n.voice_webhook_url || 'No inbound voice webhook configured'}
                              >
                                {n.voice_webhook_url ? '✓' : '—'} Voice webhook
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {n.capabilities?.sms && <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">SMS</span>}
                          {n.capabilities?.voice && <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded">Voice</span>}
                          {!n.assignedUser && (
                            <button
                              onClick={() => { setAssignPhone(n.phone); setActiveTab('user-assign'); setShowUserAssignForm(true); }}
                              className="text-xs px-2 py-1 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded font-medium"
                              title="Assign this number to a staff member"
                            >
                              Assign to staff
                            </button>
                          )}
                          {n.phone_sid && (
                            <button onClick={() => releaseNumber(n.phone_sid!)} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title="Release">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ═══════════════ USER ASSIGNMENT TAB ═══════════════ */}
      {activeTab === 'user-assign' && (
        <div className="space-y-6">
          <div className="bg-purple-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-purple-800 mb-1">Staff Number Assignment</h3>
            <p className="text-xs text-purple-700">
              Assign a dedicated phone number to each staff member so their outbound messages
              (SMS, WhatsApp, calls) come from their own number. When a member replies, the response
              routes back to the assigned staff member. Requires &quot;Per User&quot; phone allocation mode in Communication Provider settings.
            </p>
          </div>

          <div className="flex justify-end">
            <button onClick={() => setShowUserAssignForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium">
              <Plus className="w-4 h-4" /> Assign Number to Staff
            </button>
          </div>

          {/* Current user assignments */}
          {userPhoneNumbers.length === 0 ? (
            <div className="text-center py-12 text-gray-500 border border-dashed rounded-lg">
              <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No staff numbers assigned</p>
              <p className="text-sm mt-1">Assign phone numbers to individual staff for personalized outreach</p>
            </div>
          ) : (
            <div className="bg-white border rounded-lg divide-y">
              {userPhoneNumbers.map(n => (
                <div key={n.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-purple-100 rounded">
                      <User className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <span className="font-mono text-sm font-medium">{formatPhone(n.phone_number)}</span>
                      <p className="text-xs text-gray-500">
                        {n.user_name || n.user_email || `User ${n.user_id}`}
                        {n.is_primary && <span className="ml-2 text-green-600 font-medium">Primary</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {n.verification_status === 'verified' ? (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">Verified</span>
                    ) : n.verification_status === 'pending' ? (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Pending</span>
                    ) : (
                      <button
                        onClick={() => setVerifyTarget(n)}
                        className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                      >
                        Verify
                      </button>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded ${n.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {n.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-xs text-gray-400">{n.provider_type || 'twilio'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Ownership-verification modal (Verified Caller ID / SMS OTP) */}
          {verifyTarget && organizationId && (
            <VerifyNumberModal
              phoneNumberId={verifyTarget.id}
              phoneNumber={formatPhone(verifyTarget.phone_number)}
              organizationId={organizationId}
              onClose={() => setVerifyTarget(null)}
              onVerified={() => { fetchAll(); }}
            />
          )}

          {/* Assign modal */}
          {showUserAssignForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Assign Number to Staff</h3>
                  <button onClick={() => { setShowUserAssignForm(false); setAssignByo(false); }} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    {allNumbers.length > 0 && !assignByo ? (
                      <select value={assignPhone} onChange={e => setAssignPhone(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500">
                        <option value="">Select a number...</option>
                        {allNumbers.map((n, i) => (
                          <option key={i} value={n.phone}>{formatPhone(n.phone)} — {n.source}</option>
                        ))}
                      </select>
                    ) : (
                      <input type="tel" value={assignPhone} onChange={e => setAssignPhone(e.target.value)} placeholder="+15551234567"
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                    )}
                    <label className="mt-2 flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={assignByo}
                        onChange={e => { setAssignByo(e.target.checked); setAssignPhone(''); }}
                        className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                      <span>
                        Staff member&apos;s <span className="font-medium">own number</span> (not a platform number).
                        Enter it in E.164 (+1&hellip;); we&apos;ll verify ownership next — no porting needed.
                      </span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member</label>
                    {orgUsers.length > 0 ? (
                      <select value={assignUserId} onChange={e => setAssignUserId(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500">
                        <option value="">Select staff member...</option>
                        {orgUsers.map(u => (
                          <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                        ))}
                      </select>
                    ) : (
                      <input type="number" value={assignUserId} onChange={e => setAssignUserId(e.target.value)} placeholder="User ID"
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button onClick={() => { setShowUserAssignForm(false); setAssignByo(false); }} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
                  <button onClick={assignToUser} disabled={!assignPhone || !assignUserId || saving}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} {assignByo ? 'Add & Verify' : 'Assign'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════ ROUTING TAB ═══════════════ */}
      {activeTab === 'routing' && (
        <div className="space-y-6">
          <div className="bg-orange-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-orange-800 mb-1">Campaign & Program Routing</h3>
            <p className="text-xs text-orange-700">
              Route specific phone numbers to campaigns or programs. When a campaign sends messages,
              it uses the assigned number instead of the default. Replies to that number are automatically
              routed back to the correct campaign context. One number can be assigned to multiple campaigns/programs.
            </p>
          </div>

          <div className="flex justify-end">
            <button onClick={() => { setShowRoutingForm(true); setRoutingPhone(''); setRoutingSelectedIds([]); setRoutingType('organization'); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              <Plus className="w-4 h-4" /> Add Route
            </button>
          </div>

          {/* Current routes grouped by number */}
          {assignments.length === 0 ? (
            <div className="text-center py-12 text-gray-500 border border-dashed rounded-lg">
              <Link2 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No routing rules configured</p>
              <p className="text-sm mt-1">Assign numbers to campaigns or programs for message routing</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(
                assignments.reduce<Record<string, SenderAssignment[]>>((acc, a) => {
                  const key = `${a.phone_number}|${a.channel}`;
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(a);
                  return acc;
                }, {})
              ).map(([key, items]) => {
                const [phone, channel] = key.split('|');
                const badge = CHANNEL_BADGES[channel] || CHANNEL_BADGES.sms;
                return (
                  <div key={key} className="bg-white border rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-3">
                      <span className="font-mono text-sm font-semibold">{formatPhone(phone)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>{badge.label}</span>
                      <span className="text-xs text-gray-400">{items.length} route{items.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {items.map(a => {
                        const Icon = a.assignment_type === 'campaign' ? Megaphone : a.assignment_type === 'program' ? Briefcase : Building2;
                        const color = a.assignment_type === 'campaign' ? 'text-purple-600' : a.assignment_type === 'program' ? 'text-orange-600' : 'text-blue-600';
                        return (
                          <div key={a.id} className="px-4 py-2.5 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              <Icon className={`w-4 h-4 ${color}`} />
                              <span className="text-sm text-gray-700">{getAssignmentLabel(a)}</span>
                              <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{a.assignment_type}</span>
                            </div>
                            <button onClick={() => setAssignments(prev => prev.filter(x => x.id !== a.id))}
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Routing form modal */}
          {showRoutingForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Add Number Route</h3>
                  <button onClick={() => setShowRoutingForm(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-5">
                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <select value={routingPhone} onChange={e => setRoutingPhone(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                      <option value="">Select a number...</option>
                      {allNumbers.map((n, i) => (
                        <option key={i} value={n.phone}>{formatPhone(n.phone)} — {n.source}</option>
                      ))}
                    </select>
                  </div>
                  {/* Channel */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                    <div className="flex gap-2">
                      {Object.entries(CHANNEL_BADGES).map(([ch, conf]) => (
                        <button key={ch} type="button" onClick={() => setRoutingChannel(ch)}
                          className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            routingChannel === ch ? `${conf.color} border-current` : 'border-gray-300 text-gray-500'
                          }`}>
                          {conf.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Route To</label>
                    <div className="flex gap-2">
                      {[
                        { type: 'organization', label: 'Organization', icon: Building2 },
                        { type: 'campaign', label: 'Campaign', icon: Megaphone },
                        { type: 'program', label: 'Program', icon: Briefcase },
                      ].map(({ type, label, icon: Icon }) => (
                        <button key={type} type="button" onClick={() => { setRoutingType(type); setRoutingSelectedIds([]); }}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            routingType === type ? 'bg-blue-50 border-blue-500 text-blue-700' : 'border-gray-300 text-gray-500'
                          }`}>
                          <Icon className="w-4 h-4" /> {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Multi-select */}
                  {routingType === 'campaign' && campaigns.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Campaigns</label>
                      <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                        {campaigns.map(c => (
                          <label key={c.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 ${routingSelectedIds.includes(c.id) ? 'bg-purple-50' : ''}`}>
                            <input type="checkbox" checked={routingSelectedIds.includes(c.id)}
                              onChange={() => setRoutingSelectedIds(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                              className="rounded border-gray-300 text-purple-600" />
                            <Megaphone className={`w-4 h-4 ${routingSelectedIds.includes(c.id) ? 'text-purple-600' : 'text-gray-400'}`} />
                            <span className="text-sm flex-1 truncate">{c.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{c.status}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {routingType === 'program' && programs.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Programs</label>
                      <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                        {programs.map(p => (
                          <label key={p.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 ${routingSelectedIds.includes(String(p.id)) ? 'bg-orange-50' : ''}`}>
                            <input type="checkbox" checked={routingSelectedIds.includes(String(p.id))}
                              onChange={() => { const sid = String(p.id); setRoutingSelectedIds(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]); }}
                              className="rounded border-gray-300 text-orange-600" />
                            <Briefcase className={`w-4 h-4 ${routingSelectedIds.includes(String(p.id)) ? 'text-orange-600' : 'text-gray-400'}`} />
                            <span className="text-sm flex-1 truncate">{p.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {routingType === 'organization' && (
                    <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Organization-wide route</p>
                        <p className="text-xs text-gray-500">This number becomes the default sender for all outbound messages</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between">
                  <button onClick={() => setShowRoutingForm(false)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
                  <button onClick={createRouting}
                    disabled={!routingPhone || (routingType !== 'organization' && routingSelectedIds.length === 0) || saving}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    {routingType === 'organization' ? 'Set as Default' : `Route to ${routingSelectedIds.length} ${routingType}${routingSelectedIds.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PhoneNumberManagement;
